import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Loader, Eye, EyeOff } from 'lucide-react';
import { Button } from '../../../components/ui/button';
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from '../../../components/ui/select';
import SharedConfigPreview from './SharedConfigPreview';

/**
 * Reusable "assign a shared dashboard config" control: a select populated
 * from `assignableConfigs` plus a button that invokes `onAssign(id, name)`.
 * Used both per-user (UserRow, Task 8 Step 2) and for the bulk "assign to
 * everyone" action above the users list (UsersTab, Task 8 Step 3) — the
 * `label` prop lets each call site customize the button text.
 *
 * Owns its own loading/error state so a failed assignment never crashes the
 * parent row/list; the caller is only notified on success via the Promise
 * returned by `onAssign`.
 */
const AssignConfigControl = ({ assignableConfigs, onAssign, label }) => {
  const { t } = useTranslation('admin');
  const [selectedId, setSelectedId] = useState('');
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState(null);
  const [previewOpen, setPreviewOpen] = useState(false);

  const handleSelectId = (id) => {
    setSelectedId(id);
    setPreviewOpen(false);
  };

  const handleAssign = async () => {
    if (!selectedId) return;
    const config = assignableConfigs.find((c) => c.id === selectedId);
    if (!config) return;
    setLoading(true);
    setErr(null);
    try {
      await onAssign(config.id, config.name);
    } catch (e) {
      setErr(t('errors.assignConfig', { detail: e.message }));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div>
      <div className="flex items-center gap-2">
        <Select value={selectedId} onValueChange={handleSelectId} disabled={loading}>
          <SelectTrigger className="h-8 text-sm w-56">
            <SelectValue placeholder={t('assignConfig.selectPlaceholder')} />
          </SelectTrigger>
          <SelectContent>
            {assignableConfigs.map((c) => (
              <SelectItem key={c.id} value={c.id}>
                {c.name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Button
          size="sm"
          variant="outline"
          disabled={!selectedId}
          onClick={() => setPreviewOpen((prev) => !prev)}
          title={previewOpen ? t('assignConfig.hidePreview') : t('assignConfig.preview')}
        >
          {previewOpen ? <EyeOff className="w-3.5 h-3.5" /> : <Eye className="w-3.5 h-3.5" />}
        </Button>
        <Button size="sm" onClick={handleAssign} disabled={loading || !selectedId}>
          {loading && <Loader className="w-3.5 h-3.5 animate-spin mr-1.5" />}
          {label}
        </Button>
        {err && <span className="text-xs text-error-600">{err}</span>}
      </div>
      {previewOpen && selectedId && <SharedConfigPreview configId={selectedId} />}
    </div>
  );
};

export default AssignConfigControl;
